import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

/*
  Agregat: optymalizacja zysku (3 warianty)
  1) Sklep (obniżka ceny -> większa sprzedaż)
  2) Firma (podwyżka czynszu -> mniej wynajętych lokali)
  3) Jadłodajnia (obniżka ceny obiadu -> większa sprzedaż)

  Assessment zawsze ten sam:
  - podaj cenę optymalną C*
  - podaj maksymalny zysk Zmax

  Metodyka w wyjaśnieniu (czytelna i „maturalna”):
  - wprowadzamy jedną zmienną x (liczba kroków zmiany ceny)
  - zapisujemy C(x) oraz S(x)
  - zapisujemy Z(x) i sprowadzamy do postaci ogólnej: ax^2 + bx + c
  - wierzchołek: P = -b/(2a) (to jest x*)
  - C* = C(P), Zmax = Z(P)
*/

/* =========================
   Helpers
========================= */

function pluralPL(n, forms) {
  // forms: [singular, paucal(2-4), plural(5+)]
  // wyjątek: 12–14 zawsze plural
  const abs = Math.abs(Number(n));
  const mod100 = abs % 100;
  const mod10 = abs % 10;

  if (abs === 1) return forms[0];
  if (mod100 >= 12 && mod100 <= 14) return forms[2];
  if (mod10 >= 2 && mod10 <= 4) return forms[1];
  return forms[2];
}

const FORMS = {
  obiad: ["obiad", "obiady", "obiadów"],
  lokal: ["lokal", "lokale", "lokali"],
  sztuka: ["sztukę", "sztuki", "sztuk"],
};

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function parseNumber(rawValue) {
  if (rawValue == null) return null;

  let raw =
    typeof rawValue === "object" && rawValue.raw != null
      ? String(rawValue.raw)
      : String(rawValue);

  raw = raw.trim().replace(/\s+/g, "").replace(",", ".").replace(/\u2212/g, "-");
  if (raw === "") return null;

  const frac = raw.match(/^([+\-]?\d+)\s*\/\s*([+\-]?\d+)$/);
  if (frac) {
    const p = parseInt(frac[1], 10);
    const q = parseInt(frac[2], 10);
    if (!Number.isFinite(p) || !Number.isFinite(q) || q === 0) return null;
    return p / q;
  }

  if (/^[+\-]?\d+(\.\d+)?$/.test(raw)) {
    const v = parseFloat(raw);
    return Number.isFinite(v) ? v : null;
  }

  return null;
}

function round2(x) {
  return Math.round(x * 100) / 100;
}

/* =========================
   Generatory wariantów
========================= */

function genShop() {
  const d = pick([5, 10, 20]); // zł (obniżka ceny)
  const u = pick([1, 2, 3, 4]); // sztuk (wzrost sprzedaży)
  const q0 = pick([20, 30, 40, 50, 60, 70, 80]);
  const cost = pick([900, 1000, 1100, 1200, 1300, 1400, 1500]);

  // Chcemy, żeby optimum (p = x*) było całkowite.
  // Dla Z(x) = (p0 - d x - cost)(q0 + u x) mamy:
  // a = -d*u
  // b = u(p0 - cost) - d*q0
  // p = -b/(2a) będzie całkowite, gdy dobierzemy p0 pod parametry.
  //
  // Ustawiamy:
  // u(p0 - cost) - d*q0 = 2*d*u*p
  // => p0 = cost + (d*q0)/u + 2*d*p
  //
  // Żeby uniknąć ułamków, robimy q0 wielokrotność u.
  const q0Fixed = q0 - (q0 % u);

  const kStar = pick([0, 1, 2, 3, 4, 5, 6, 8, 10, 12]); // p = x* (liczba obniżek)
  const p0 = cost + (d * q0Fixed) / u + 2 * d * kStar;

  const cStar = p0 - d * kStar;          // C*
  const qStar = q0Fixed + u * kStar;     // S(p)
  const zMax = (cStar - cost) * qStar;

  return {
    variant: "shop",
    params: { cost, q0: q0Fixed, p0, d, u },
    solution: { kStar, cStar, qStar, zMax },
    meta: { maxPoints: 2, unit: "miesiąc" },
  };
}


function genRentals() {
  const s = pick([20, 30, 40, 50]); // zł
  const m = pick([3, 4, 5, 6]); // lokale
  const t = pick([30, 35, 40, 45, 50]);
  const N = m * t;

  const maxK = Math.floor((N - 1) / m);
  const kStar = pick([0, 1, 2, 3, 4, 5].filter((k) => k <= maxK));

  const p0 = Math.max(s * (N / m - 2 * kStar), 600);

  const cStar = p0 + s * kStar; // C*
  const nStar = N - m * kStar;  // S(P)
  const zMax = cStar * nStar;

  return {
    variant: "rentals",
    params: { N, p0, s, m },
    solution: { kStar, cStar, nStar, zMax },
    meta: { maxPoints: 2, unit: "miesiąc" },
  };
}

function genCanteen() {
  const d = pick([1, 2, 3]); // zł
  const u = pick([2, 3, 4]); // obiadów
  const q0 = pick([80, 100, 120, 140, 160]);
  const costMeal = pick([10, 11, 12, 13, 14, 15]);

  const q0Fixed = q0 - (q0 % u);

  const kStar = pick([0, 1, 2, 3, 4, 5, 6, 8, 10]);

  const p0 = costMeal + (d * q0Fixed) / u + 2 * d * kStar;

  const cStar = p0 - d * kStar;      // C*
  const qStar = q0Fixed + u * kStar; // S(P)
  const zMax = (cStar - costMeal) * qStar;

  return {
    variant: "canteen",
    params: { costMeal, q0: q0Fixed, p0, d, u },
    solution: { kStar, cStar, qStar, zMax },
    meta: { maxPoints: 2, unit: "dzień" },
  };
}

/* =========================
   Public generator
========================= */

export function generateTask() {
  const variant = pick(["shop", "rentals", "canteen"]);

  const payload =
    variant === "shop"
      ? genShop()
      : variant === "rentals"
      ? genRentals()
      : genCanteen();

  return {
    type: "ProfitOptimizationStoryTask",
    data: {
      variant: payload.variant,
      params: payload.params,
      solution: {
        ...payload.solution,
        cStar: round2(payload.solution.cStar),
        zMax: round2(payload.solution.zMax),
      },
      maxPoints: payload.meta.maxPoints,
      unit: payload.meta.unit,
    },
  };
}

/* =========================
   Render task
========================= */

export function renderTask({ data }) {
  const { variant, params } = data;

  if (variant === "shop") {
    const { cost, q0, p0, d, u } = params;

    return (
      <div style={{ textAlign: "left", lineHeight: 1.6 }}>
        <p><strong>Zadanie optymalizacyjne (sklep).</strong></p>

        <p>
          Właściciel sklepu kupuje towar w cenie <InlineMath math={`${cost}`} /> zł za sztukę.
          Obecnie sprzedaje miesięcznie <InlineMath math={`${q0}`} /> sztuk w cenie <InlineMath math={`${p0}`} /> zł za sztukę.
          Oszacowano, że każda obniżka ceny o <InlineMath math={`${d}`} /> zł zwiększa sprzedaż o{" "}
            <InlineMath math={`${u}`} /> {pluralPL(u, FORMS.sztuka)} miesięcznie.
        </p>

        <p>
          Jaką cenę powinien ustalić, aby jego <strong>miesięczny zysk</strong> był największy?
          Oblicz też ten maksymalny zysk.
        </p>
      </div>
    );
  }

  if (variant === "rentals") {
    const { N, p0, s, m } = params;

    return (
      <div style={{ textAlign: "left", lineHeight: 1.6 }}>
        <p><strong>Zadanie optymalizacyjne (wynajem lokali).</strong></p>

        <p>
          Firma wynajmuje lokale użytkowe. Obecnie wynajmuje <InlineMath math={`${N}`} /> lokali,
          a miesięczna opłata za jeden lokal wynosi <InlineMath math={`${p0}`} /> zł.
          Oszacowano, że każda podwyżka ceny o <InlineMath math={`${s}`} /> zł spowoduje zmniejszenie liczby wynajętych lokali o{" "}
          <InlineMath math={`${m}`} /> {pluralPL(m, FORMS.lokal)}.
        </p>

        <p>
          Jaką miesięczną cenę za jeden lokal powinna ustalić firma, aby jej <strong>miesięczny zysk</strong> był największy?
          Oblicz też ten maksymalny zysk.
        </p>
      </div>
    );
  }

  const { costMeal, q0, p0, d, u } = params;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.6 }}>
      <p><strong>Zadanie optymalizacyjne (jadłodajnia).</strong></p>

      <p>
        Jadłodajnia sprzedaje obiady. Koszt przygotowania jednego obiadu wynosi <InlineMath math={`${costMeal}`} /> zł.
        Obecnie sprzedaje dziennie <InlineMath math={`${q0}`} /> obiadów w cenie <InlineMath math={`${p0}`} /> zł za obiad.
        Oszacowano, że każda obniżka ceny o <InlineMath math={`${d}`} /> zł zwiększa sprzedaż o{" "}
        <InlineMath math={`${u}`} /> {pluralPL(u, FORMS.obiad)} dziennie.
      </p>

      <p>
        Jaką cenę powinna ustalić jadłodajnia, aby jej <strong>dzienny zysk</strong> był największy?
        Oblicz też ten maksymalny zysk.
      </p>
    </div>
  );
}

/* =========================
   Input
========================= */

export function renderInput(_task, userAnswer, onChange, disabled) {
  const ua = userAnswer || {};

  return (
    <div style={{ display: "grid", rowGap: 10, maxWidth: 460 }}>
      <div style={{ marginBottom: 2 }}>
        <strong>Wpisz:</strong>
      </div>

      <div style={{ display: "grid", rowGap: 6, marginTop: -2 }}>
        <div>
          <InlineMath math={'C^*'} /> =
        </div>

        <FractionInput
          value={ua.c}
          onChange={(v) => onChange({ ...(ua || {}), c: v })}
          disabled={disabled}
          label={'C^*'}
          suffixLatex={'\\text{zł}'}
        />

        <div style={{ marginTop: 2 }}>
          <InlineMath math={'Z_{\\max}'} /> =
        </div>

        <FractionInput
          value={ua.Z}
          onChange={(v) => onChange({ ...(ua || {}), Z: v })}
          disabled={disabled}
          label={'Z_{\\max}'}
          suffixLatex={'\\text{zł}'}
        />
      </div>
    </div>
  );
}

/* =========================
   Check
========================= */

export function checkAnswer({ data }, userAnswer) {
  const c = parseNumber(userAnswer?.c);
  const Z = parseNumber(userAnswer?.Z);

  const cOk = c != null && Math.abs(c - data.solution.cStar) < 1e-9;
  const zOk = Z != null && Math.abs(Z - data.solution.zMax) < 1e-9;

  const parts = { "C*": cOk, "Zmax": zOk };
  const score = (cOk ? 1 : 0) + (zOk ? 1 : 0);

  return { isCorrect: score === 2, score, maxScore: 2, parts };
}


/* =========================
   Answer + User answer
========================= */

export function renderAnswer({ data }) {
  const { cStar, zMax } = data.solution;

  return (
    <div style={{ textAlign: "left" }}>
      <div><InlineMath math={`C^*=${cStar}\\ \\text{zł}`} /></div>
      <div style={{ marginTop: 6 }}><InlineMath math={`Z_{\\max}=${zMax}\\ \\text{zł}`} /></div>
    </div>
  );
}

export function renderUserAnswer(taskOrAnswer, maybeAnswer) {
  const answer = maybeAnswer ?? taskOrAnswer;
  if (!answer?.c && !answer?.Z) return <i>(brak odpowiedzi)</i>;

  const cRaw =
    typeof answer.c === "object" ? answer.c?.raw ?? "" : String(answer.c ?? "");
  const zRaw =
    typeof answer.Z === "object" ? answer.Z?.raw ?? "" : String(answer.Z ?? "");

  const cLatex = cRaw.trim() ? cRaw.trim() : "\\text{–}";
  const zLatex = zRaw.trim() ? zRaw.trim() : "\\text{–}";

  return (
    <div style={{ textAlign: "left" }}>
      <div><InlineMath math={`C^*=${cLatex}\\ \\text{zł}`} /></div>
      <div style={{ marginTop: 6 }}><InlineMath math={`Z_{\\max}=${zLatex}\\ \\text{zł}`} /></div>
    </div>
  );
}

/* =========================
   Explanation (czytelnie: C(x), S(x), Z(x) i P = -b/(2a))
========================= */

export function renderExplanation({ data }) {
  const { variant, params, solution } = data;
  const { kStar, cStar, zMax } = solution;
  const p = kStar;

  const stepTitle = (n, text) => (
    <p style={{ margin: "10px 0 6px" }}>
      <strong>Krok {n}:</strong> {text}
    </p>
  );

  const polyLatex = (a, b, c) => {
    const bPart = b === 0 ? "" : (b > 0 ? `+${b}x` : `${b}x`);
    const cPart = c === 0 ? "" : (c > 0 ? `+${c}` : `${c}`);
    return `Z(x)=${a}x^2${bPart}${cPart}.`;
  };

  // ===== SHOP =====
  if (variant === "shop") {
    const { cost, q0, p0, d, u } = params;
    const { qStar } = solution;

    const A = p0 - cost;
    const a = -d * u;
    const b = u * (p0 - cost) - d * q0;
    const c0 = (p0 - cost) * q0;

    return (
      <div style={{ textAlign: "left", lineHeight: 1.75, marginTop: 8 }}>
        {stepTitle(1, (
          <>Ustalamy <InlineMath math={'x'} /> – liczbę obniżek o <InlineMath math={`${d}`} /> zł i zapisujemy funkcje:</>
        ))}
        <BlockMath math={`C(x)=${p0}-${d}x`} />
        <BlockMath math={`S(x)=${q0}+${u}x`} />

        {stepTitle(2, "Zysk to (cena − koszt) · sprzedaż, więc:")}
        <BlockMath math={`C(x)-${cost}=(${p0}-${d}x)-${cost}=${A}-${d}x`} />
        <BlockMath math={`Z(x)=(${A}-${d}x)(${q0}+${u}x)`} />

        {stepTitle(3, "Rozwijamy nawiasy, redukujemy wyrazy podobne i dostajemy postać ogólną:")}
        <BlockMath math={`Z(x)=${A}\\cdot${q0}+${A}\\cdot${u}x-${d}x\\cdot${q0}-${d}x\\cdot${u}x`} />
        <BlockMath math={`Z(x)=${c0}+(${A * u}-${d * q0})x+(${a})x^2`} />
        <BlockMath math={polyLatex(a, b, c0)} />

        {stepTitle(4, (
          <>Wierzchołek: <InlineMath math={'p=-\\frac{b}{2a}'} />, więc:</>
        ))}
        <BlockMath math={`p=-\\frac{${b}}{2\\cdot(${a})}=${p}.`} />

        {stepTitle(5, "Podstawiamy p i odczytujemy wynik:")}
        <BlockMath math={`C^*=C(p)=${p0}-${d}\\cdot${p}=${cStar}\\ \\text{zł}.`} />
        <BlockMath math={`S(p)=${q0}+${u}\\cdot${p}=${qStar}.`} />
        <BlockMath math={`Z_{\\max}=Z(p)=${zMax}\\ \\text{zł}.`} />

        <p style={{ marginTop: 10 }}>
          <strong>Wniosek:</strong> <InlineMath math={`C^*=${cStar}`} /> zł, a{" "}
          <InlineMath math={`Z_{\\max}=${zMax}`} /> zł miesięcznie.
        </p>
      </div>
    );
  }

  // ===== RENTALS =====
  if (variant === "rentals") {
    const { N, p0, s, m } = params;
    const { nStar } = solution;

    const a = -s * m;
    const b = s * N - m * p0;
    const c0 = p0 * N;

    return (
      <div style={{ textAlign: "left", lineHeight: 1.75, marginTop: 8 }}>
        {stepTitle(1, (
          <>Ustalamy <InlineMath math={'x'} /> – liczbę podwyżek o <InlineMath math={`${s}`} /> zł i zapisujemy funkcje:</>
        ))}
        <BlockMath math={`C(x)=${p0}+${s}x`} />
        <BlockMath math={`S(x)=${N}-${m}x`} />

        {stepTitle(2, "W tym wariancie nie uwzględniamy dodatkowych kosztów, więc zysk/przychód to C(x)·S(x):")}
        <BlockMath math={`Z(x)=(${p0}+${s}x)(${N}-${m}x)`} />

        {stepTitle(3, "Rozwijamy nawiasy, redukujemy i zapisujemy postać ogólną:")}
        <BlockMath math={`Z(x)=${p0}\\cdot${N}-${p0}\\cdot${m}x+${s}x\\cdot${N}-${s}x\\cdot${m}x`} />
        <BlockMath math={`Z(x)=${c0}+(${s * N}-${m * p0})x+(${a})x^2`} />
        <BlockMath math={polyLatex(a, b, c0)} />

        {stepTitle(4, (
          <>Wierzchołek: <InlineMath math={'p=-\\frac{b}{2a}'} />, więc:</>
        ))}
        <BlockMath math={`p=-\\frac{${b}}{2\\cdot(${a})}=${p}.`} />

        {stepTitle(5, "Podstawiamy p i odczytujemy wynik:")}
        <BlockMath math={`C^*=C(p)=${p0}+${s}\\cdot${p}=${cStar}\\ \\text{zł}.`} />
        <BlockMath math={`S(p)=${N}-${m}\\cdot${p}=${nStar}.`} />
        <BlockMath math={`Z_{\\max}=Z(p)=${zMax}\\ \\text{zł}.`} />

        <p style={{ marginTop: 10 }}>
          <strong>Wniosek:</strong> <InlineMath math={`C^*=${cStar}`} /> zł, a{" "}
          <InlineMath math={`Z_{\\max}=${zMax}`} /> zł miesięcznie.
        </p>
      </div>
    );
  }

  // ===== CANTEEN =====
  const { costMeal, q0, p0, d, u } = params;
  const { qStar } = solution;

  const A = p0 - costMeal;
  const a = -d * u;
  const b = u * (p0 - costMeal) - d * q0;
  const c0 = (p0 - costMeal) * q0;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.75, marginTop: 8 }}>
      {stepTitle(1, (
        <>Ustalamy <InlineMath math={'x'} /> – liczbę obniżek o <InlineMath math={`${d}`} /> zł i zapisujemy funkcje:</>
      ))}
      <BlockMath math={`C(x)=${p0}-${d}x`} />
      <BlockMath math={`S(x)=${q0}+${u}x`} />

      {stepTitle(2, "Zysk to (cena − koszt) · sprzedaż, więc:")}
      <BlockMath math={`C(x)-${costMeal}=(${p0}-${d}x)-${costMeal}=${A}-${d}x`} />
      <BlockMath math={`Z(x)=(${A}-${d}x)(${q0}+${u}x)`} />

      {stepTitle(3, "Rozwijamy nawiasy, redukujemy i zapisujemy postać ogólną:")}
      <BlockMath math={`Z(x)=${A}\\cdot${q0}+${A}\\cdot${u}x-${d}x\\cdot${q0}-${d}x\\cdot${u}x`} />
      <BlockMath math={`Z(x)=${c0}+(${A * u}-${d * q0})x+(${a})x^2`} />
      <BlockMath math={polyLatex(a, b, c0)} />

      {stepTitle(4, (
        <>Wierzchołek: <InlineMath math={'p=-\\frac{b}{2a}'} />, więc:</>
      ))}
      <BlockMath math={`p=-\\frac{${b}}{2\\cdot(${a})}=${p}.`} />

      {stepTitle(5, "Podstawiamy p i odczytujemy wynik:")}
      <BlockMath math={`C^*=C(p)=${p0}-${d}\\cdot${p}=${cStar}\\ \\text{zł}.`} />
      <BlockMath math={`S(p)=${q0}+${u}\\cdot${p}=${qStar}.`} />
      <BlockMath math={`Z_{\\max}=Z(p)=${zMax}\\ \\text{zł}.`} />

      <p style={{ marginTop: 10 }}>
        <strong>Wniosek:</strong> <InlineMath math={`C^*=${cStar}`} /> zł, a{" "}
        <InlineMath math={`Z_{\\max}=${zMax}`} /> zł dziennie.
      </p>
    </div>
  );
}

export default {
  generateTask,
  renderTask,
  renderInput,
  checkAnswer,
  renderAnswer,
  renderExplanation,
  renderUserAnswer,
};
